import { useEffect, useRef } from "react";

import type { PlaygroundSessionState } from "src/agents/AgentVersionPage/PlaygroundContext";
import { toastActions } from "src/design-system/Toast/utils";

type UsePlaygroundBackgroundToastParams = {
  sessionState: PlaygroundSessionState;
  isDrawerOpen: boolean;
  onOpenDrawer: () => void;
};

export const usePlaygroundBackgroundToast = ({
  sessionState,
  isDrawerOpen,
  onOpenDrawer,
}: UsePlaygroundBackgroundToastParams): void => {
  const toastIdRef = useRef<string | null>(null);

  useEffect(() => {
    // Drawer reopened — dismiss any active toast
    if (isDrawerOpen && toastIdRef.current) {
      toastActions.dismiss(toastIdRef.current);
      toastIdRef.current = null;
      return;
    }

    // Session reset to idle while toast is visible (e.g. invoke error)
    if (sessionState.status === "idle" && toastIdRef.current) {
      toastActions.dismiss(toastIdRef.current);
      toastIdRef.current = null;
      return;
    }

    // Drawer closed + session active + no toast yet → show loading toast
    if (
      !isDrawerOpen &&
      sessionState.status === "active" &&
      !toastIdRef.current
    ) {
      const toastId = `playground-bg-${sessionState.sessionId}`;
      toastIdRef.current = toastId;
      toastActions.loading({
        id: toastId,
        title: "Agent testing in progress",
        description:
          "Test is still running. Results will appear when the test is complete.",
        duration: null,
        actionText: "View test run",
        onActionClick: onOpenDrawer,
      });
      return;
    }

    // Session reached terminal state while toast is visible → update toast
    if (
      !isDrawerOpen &&
      sessionState.status === "stopped" &&
      toastIdRef.current
    ) {
      const toastId = toastIdRef.current;
      toastIdRef.current = null;

      if (sessionState.substatus === "success") {
        toastActions.success({
          id: toastId,
          title: "Agent test completed",
          description: "Your test run finished successfully.",
          actionText: "View test run",
          onActionClick: onOpenDrawer,
        });
      } else if (sessionState.substatus === "failed") {
        toastActions.failure({
          id: toastId,
          title: "Agent test failed",
          description:
            sessionState.terminationMessage ?? "The test run failed.",
          actionText: "View test run",
          onActionClick: onOpenDrawer,
        });
      } else {
        // cancelled
        toastActions.default({
          id: toastId,
          title: "Agent test cancelled",
        });
      }
    }
  }, [sessionState, isDrawerOpen, onOpenDrawer]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (toastIdRef.current) {
        toastActions.dismiss(toastIdRef.current);
        toastIdRef.current = null;
      }
    };
  }, []);
};
